<script setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  availabilityHours: { type: Array, default: () => [] },
  availabilityLoading: { type: Boolean, default: false },
  deedHours: { type: Array, default: () => [] },
  value: { type: Object, required: true },
});

const emit = defineEmits(['update:value']);
const { t } = useI18n();

const hours = computed(() => {
  const source = props.availabilityHours.length
    ? props.availabilityHours
    : props.deedHours.map(hora => ({ hora, permisao: 1 }));
  return source
    .map(item => ({
      hora: String(item.hora || '').trim(),
      permisao: [true, 1, '1'].includes(item.permisao),
    }))
    .filter(item => item.hora);
});
const dateReady = computed(() =>
  /^\d{2}\/\d{2}\/\d{4}$/.test(props.value.data_escritura || '')
);

const update = (field, value) =>
  emit('update:value', { ...props.value, [field]: value });
const normalizeDate = value =>
  value
    .replace(/\D/g, '')
    .slice(0, 8)
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\d{2}\/\d{2})(\d)/, '$1/$2');
const normalizeTime = value =>
  value
    .replace(/\D/g, '')
    .slice(0, 4)
    .replace(/(\d{2})(\d{2})$/, '$1:$2');

const updateDate = value => {
  const date = normalizeDate(value);
  emit('update:value', {
    ...props.value,
    data_escritura: date,
    hora_escritura:
      date === props.value.data_escritura ? props.value.hora_escritura : '',
  });
};
const updateTime = value => {
  const option = hours.value.find(item => item.hora === value);
  if (!option || !option.permisao) return;

  update('hora_escritura', value);
};
const hourLabel = hour =>
  hour.permisao
    ? hour.hora
    : `${hour.hora} - ${t('POST_SALE.FORM.DEED.BOOKED')}`;

watch(
  () => [props.value.hora_escritura, hours.value],
  ([selected, options]) => {
    if (
      selected &&
      options.length &&
      !options.some(item => item.hora === selected && item.permisao)
    ) {
      update('hora_escritura', '');
    }
  },
  { deep: true }
);
</script>

<template>
  <section class="grid gap-4">
    <h2 class="text-lg font-semibold text-lais-text">
      {{ t('POST_SALE.FORM.DEED.DATE_TITLE') }}
    </h2>
    <div class="grid gap-4 sm:grid-cols-2">
      <label class="grid gap-1 text-sm font-semibold text-lais-text-body">
        {{ t('POST_SALE.FORM.DEED.DATE') }}
        <input
          :value="value.data_escritura"
          inputmode="numeric"
          maxlength="10"
          :placeholder="t('POST_SALE.FORM.DEED.DATE_PLACEHOLDER')"
          class="min-h-10 rounded-lais border border-lais-border px-2 font-normal"
          @input="updateDate($event.target.value)"
        />
      </label>
      <label class="grid gap-1 text-sm font-semibold text-lais-text-body">
        {{ t('POST_SALE.FORM.DEED.TIME') }}
        <div class="relative">
          <select
            v-if="hours.length"
            :value="value.hora_escritura"
            :disabled="!dateReady || availabilityLoading"
            :aria-busy="availabilityLoading"
            class="min-h-10 w-full rounded-lais border border-lais-border bg-white px-2 pr-9 font-normal"
            @change="updateTime($event.target.value)"
          >
            <option value="">{{ t('POST_SALE.FORM.SELECT') }}</option>
            <option
              v-for="hour in hours"
              :key="hour.hora"
              :value="hour.hora"
              :disabled="!hour.permisao"
            >
              {{ hourLabel(hour) }}
            </option>
          </select>
          <input
            v-else
            :value="value.hora_escritura"
            :disabled="!dateReady || availabilityLoading"
            inputmode="numeric"
            maxlength="5"
            :placeholder="t('POST_SALE.FORM.DEED.TIME_PLACEHOLDER')"
            class="min-h-10 w-full rounded-lais border border-lais-border px-2 pr-9 font-normal"
            @input="
              update('hora_escritura', normalizeTime($event.target.value))
            "
          />
          <span
            v-if="availabilityLoading"
            class="i-lucide-loader-circle pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin text-lais-primary"
            aria-hidden="true"
          />
        </div>
      </label>
    </div>
  </section>
</template>
